import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { useCurrentPortalPaused } from '../lib/maintenance';

// Detect device type (lightweight, no libs)
function getDevice() {
  const ua = navigator.userAgent || '';
  if (/Mobi|Android|iPhone|iPad|iPod/i.test(ua)) {
    return /iPad|Tablet/i.test(ua) ? 'tablet' : 'mobile';
  }
  return 'desktop';
}

function getBrowser() {
  const ua = navigator.userAgent || '';
  if (ua.includes('Edg/')) return 'Edge';
  if (ua.includes('Chrome/')) return 'Chrome';
  if (ua.includes('Safari/')) return 'Safari';
  if (ua.includes('Firefox/')) return 'Firefox';
  return 'Other';
}

// Fire-and-forget: never block UI on telemetry
function sendEvent(payload) {
  try {
    const url = `${config.API}/telemetry`;
    const body = JSON.stringify(payload);
    // Prefer sendBeacon for session_end (survives unload)
    if (payload.event_type === 'session_end' && navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
      return;
    }
    axios.post(url, payload, { timeout: 3000 }).catch(() => {});
  } catch { /* swallow */ }
}

/**
 * Global telemetry tracker — mount once inside Router.
 * - Logs page_view on every pathname change
 * - Logs session_end with total time on unload
 * Skips /admin routes (no need to track staff).
 */
export function TelemetryTracker() {
  const location = useLocation();
  const paused = useCurrentPortalPaused();
  const sessionStart = useRef(Date.now());
  const lastPath = useRef(null);

  useEffect(() => {
    // Skip admin pages
    if (location.pathname.startsWith('/admin') || paused) {
      lastPath.current = null;
      return;
    }
    if (lastPath.current === location.pathname) return;
    lastPath.current = location.pathname;

    sendEvent({
      event_type: 'page_view',
      pathname: location.pathname,
      device: getDevice(),
      browser: getBrowser(),
      referrer: document.referrer || null,
    });
  }, [location.pathname, paused]);

  useEffect(() => {
    // Fire session_end on page hide / unload
    const onHide = () => {
      // Skip if the last active path was admin
      if (lastPath.current === null) return;
      const duration = Math.round((Date.now() - sessionStart.current) / 1000);
      if (duration < 2) return; // ignore instant bounces
      sendEvent({
        event_type: 'session_end',
        pathname: lastPath.current,
        duration,
        device: getDevice(),
        browser: getBrowser(),
      });
    };
    window.addEventListener('pagehide', onHide);
    window.addEventListener('beforeunload', onHide);
    return () => {
      window.removeEventListener('pagehide', onHide);
      window.removeEventListener('beforeunload', onHide);
    };
  }, []);

  return null;
}

// Imperative helper — for button click logging
export function trackButtonClick(buttonName, extra = {}) {
  if (typeof window === 'undefined') return;
  if (window.location.pathname.startsWith('/admin')) return;
  sendEvent({
    event_type: 'button_click',
    pathname: window.location.pathname,
    device: getDevice(),
    browser: getBrowser(),
    meta: { button: buttonName, ...extra },
  });
}
